import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import {
  validateVibeUploadFile,
  VIBE_IMAGE_MAX_MB,
  VIBE_VIDEO_MAX_MB,
} from '../utils/mediaUtils';
import '../styles/StatusUpload.css';
import '../styles/VibeUploadModal.css';

const vibeUploadAxiosConfig = {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: 120000,
  maxContentLength: 30 * 1024 * 1024,
  maxBodyLength: 30 * 1024 * 1024,
};

function VibeUploadModal({
  isOpen,
  onClose,
  user,
  socket,
  onSuccess,
  variant = 'chats',
  initialType = 'text',
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [statusText, setStatusText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState(initialType);
  const [duration, setDuration] = useState(24);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraFacingMode, setCameraFacingMode] = useState('user');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStreamRef = useRef(null);

  const resetForm = () => {
    setStatusText('');
    setSelectedFile(null);
    setUploadType(initialType || 'text');
    setDuration(24);
    setUploading(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setShowPreviewModal(false);
  };

  const handleClose = () => {
    stopCamera();
    setShowCameraModal(false);
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      resetForm();
      setUploadType(initialType || 'text');
    }
    return () => {
      stopCamera();
    };
  }, [isOpen, initialType]);

  useEffect(() => {
    if (showCameraModal && videoRef.current && cameraStream) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [showCameraModal, cameraStream]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const validation = validateVibeUploadFile(file);
    if (!validation.ok) {
      alert(validation.message);
      if (event.target) event.target.value = '';
      return;
    }
    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      setUploadType('image');
    } else if (file.type.startsWith('video/')) {
      setUploadType('video');
    }
    const url = URL.createObjectURL(file);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(url);
    setShowPreviewModal(true);
    if (event.target) event.target.value = '';
  };

  const handleUpload = async () => {
    if (!statusText.trim() && !selectedFile) return;

    if (selectedFile) {
      const validation = validateVibeUploadFile(selectedFile);
      if (!validation.ok) {
        alert(validation.message);
        return;
      }
    }

    setUploading(true);
    try {
      const statusData = {
        userId: user.id,
        text: statusText.trim(),
        type: uploadType,
        duration,
      };

      if (selectedFile) {
        const formData = new FormData();
        formData.append('statusFile', selectedFile);
        const uploadResponse = await axios.post(
          `${config.apiUrl}/api/status/upload`,
          formData,
          vibeUploadAxiosConfig
        );
        statusData.fileUrl = uploadResponse.data.fileUrl;
        statusData.fileType = selectedFile.type;
      }

      const response = await axios.post(`${config.apiUrl}/api/status/create`, statusData);

      if (socket && response.data._id) {
        socket.emit('statusUploaded', {
          userId: user.id,
          statusId: response.data._id,
        });
      }

      resetForm();
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Failed to upload vibe:', error);
      const msg =
        error.response?.data?.error ||
        (error.code === 'ECONNABORTED'
          ? 'Upload timed out. Try a shorter video or check your connection.'
          : 'Failed to upload vibe. Please try again.');
      alert(msg);
    } finally {
      setUploading(false);
    }
  };

  const stopCamera = () => {
    const activeStream = cameraStreamRef.current || cameraStream;
    if (activeStream) {
      activeStream.getTracks().forEach((track) => track.stop());
    }
    cameraStreamRef.current = null;
    setCameraStream(null);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const startCamera = async (mode = 'user') => {
    try {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      let stream;
      const base = { width: { ideal: 1280 }, height: { ideal: 720 } };

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { ...base, facingMode: { exact: mode } },
        });
      } catch {
        let targetCamera = null;
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const cameras = devices.filter((d) => d.kind === 'videoinput');
          if (mode === 'environment') {
            targetCamera = cameras.find((c) => /back|rear|environment/i.test(c.label));
          } else {
            targetCamera = cameras.find((c) => /front|user|facetime/i.test(c.label));
          }
        } catch (_) {}

        if (targetCamera) {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { ...base, deviceId: { exact: targetCamera.deviceId } },
          });
        } else {
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              video: { ...base, facingMode: mode },
            });
          } catch {
            stream = await navigator.mediaDevices.getUserMedia({ video: true });
          }
        }
      }

      cameraStreamRef.current = stream;
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Could not access camera. Please check permissions.');
    }
  };

  const openCameraModal = () => {
    setShowCameraModal(true);
    setCameraFacingMode('user');
    startCamera('user');
  };

  const closeCameraModal = () => {
    setShowCameraModal(false);
    stopCamera();
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `camera-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setSelectedFile(file);
      setUploadType('image');
      closeCameraModal();
    }, 'image/jpeg', 0.85);
  };

  const switchCamera = async () => {
    const nextMode = cameraFacingMode === 'user' ? 'environment' : 'user';
    setCameraFacingMode(nextMode);
    await startCamera(nextMode);
  };

  const closePreviewModal = () => {
    setShowPreviewModal(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const cancelUploadFromPreview = () => {
    setSelectedFile(null);
    setUploadType('text');
    closePreviewModal();
  };

  if (!isOpen && !showCameraModal && !showPreviewModal) {
    return null;
  }

  const rootClass = `vibe-upload-root vibe-upload-root--${variant}`;

  return (
    <>
      {isOpen && !showPreviewModal && !showCameraModal && (
        <div
          className={`status-upload-modal ${rootClass}`}
          onClick={handleClose}
          role="presentation"
        >
          <div
            className="modal-content vibe-upload-modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="vibe-upload-title"
          >
            <div className="modal-header vibe-upload-header">
              <div className="vibe-upload-header-text">
                <span className="vibe-upload-eyebrow">Share your moment</span>
                <h3 id="vibe-upload-title">Add to my vibe</h3>
              </div>
              <button type="button" className="close-btn" onClick={handleClose} aria-label="Close">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body vibe-upload-body">
              <div className="upload-type-buttons vibe-upload-type-grid">
                <button
                  type="button"
                  className={`upload-type-btn ${uploadType === 'text' ? 'active' : ''}`}
                  onClick={() => setUploadType('text')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <path d="M14 2v6h6" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  Text
                </button>
                <button type="button" className="upload-type-btn" onClick={openCameraModal}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  Camera
                </button>
                <button
                  type="button"
                  className={`upload-type-btn ${uploadType === 'image' ? 'active' : ''}`}
                  onClick={() => {
                    setUploadType('image');
                    setTimeout(() => fileInputRef.current?.click(), 0);
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                  Image
                </button>
                <button
                  type="button"
                  className={`upload-type-btn ${uploadType === 'video' ? 'active' : ''}`}
                  onClick={() => {
                    setUploadType('video');
                    setTimeout(() => fileInputRef.current?.click(), 0);
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="23 7 16 12 23 17 23 7" />
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </svg>
                  Video
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept={uploadType === 'video' ? 'video/*' : 'image/*'}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              <div className="status-text-input vibe-upload-text-wrap">
                <textarea
                  placeholder="Type a vibe message..."
                  value={statusText}
                  onChange={(e) => setStatusText(e.target.value)}
                  rows="4"
                />
              </div>

              {(uploadType === 'image' || uploadType === 'video') && (
                <div className="file-upload-section vibe-upload-file-section">
                  <p className="vibe-upload-size-hint">
                    {uploadType === 'video'
                      ? `Videos up to ${VIBE_VIDEO_MAX_MB}MB`
                      : `Images up to ${VIBE_IMAGE_MAX_MB}MB`}
                  </p>
                  <button
                    type="button"
                    className="file-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {selectedFile ? selectedFile.name : `Choose ${uploadType}`}
                  </button>
                  {selectedFile && (
                    <div className="selected-file">
                      <span>{selectedFile.name}</span>
                      <button
                        type="button"
                        className="remove-file-btn"
                        onClick={() => setSelectedFile(null)}
                        aria-label="Remove file"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="duration-selection-section vibe-upload-duration">
                <label className="duration-label">Vibe duration</label>
                <div className="duration-options vibe-duration-options">
                  {[
                    { hours: 1, label: '1 Hour' },
                    { hours: 24, label: '1 Day' },
                    { hours: 72, label: '3 Days' },
                    { hours: 168, label: '1 Week' },
                  ].map(({ hours, label }) => (
                    <button
                      key={hours}
                      type="button"
                      className={`duration-option ${duration === hours ? 'active' : ''}`}
                      onClick={() => setDuration(hours)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="modal-actions vibe-upload-actions">
                <button type="button" className="cancel-btn vibe-upload-cancel" onClick={handleClose}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="upload-btn vibe-upload-submit"
                  onClick={handleUpload}
                  disabled={uploading || (!statusText.trim() && !selectedFile)}
                >
                  {uploading ? 'Uploading...' : 'Add to my vibe'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCameraModal && (
        <div className={`camera-modal ${rootClass}`}>
          <div className="camera-content" onClick={(e) => e.stopPropagation()}>
            <div className="camera-header vibe-upload-header">
              <h3>Take Photo</h3>
              <button type="button" className="close-btn" onClick={closeCameraModal} aria-label="Close camera">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="camera-body">
              <div className="camera-preview">
                <video ref={videoRef} autoPlay playsInline muted className="camera-video" />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>
              <div className="camera-controls">
                <button type="button" className="camera-switch-btn" onClick={switchCamera} title="Switch camera">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 8l4-4m0 0l-4-4m4 4H7a4 4 0 000 8h.01" />
                    <path d="M7 16l-4 4m0 0l4 4m-4-4h10a4 4 0 000-8h-.01" />
                  </svg>
                </button>
                <button type="button" className="camera-capture-btn" onClick={capturePhoto} title="Take photo">
                  <div className="capture-ring">
                    <div className="capture-circle" />
                  </div>
                </button>
                <div className="camera-placeholder" />
              </div>
            </div>
          </div>
        </div>
      )}

      {showPreviewModal && selectedFile && (
        <div className={`status-upload-modal ${rootClass}`} onClick={closePreviewModal} role="presentation">
          <div className="modal-content preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header vibe-upload-header">
              <h3>Preview {uploadType === 'image' ? 'Image' : 'Video'}</h3>
              <button type="button" className="close-btn" onClick={closePreviewModal} aria-label="Close preview">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="modal-body preview-body">
              <div className="preview-container">
                {uploadType === 'image' ? (
                  <img src={previewUrl} alt="Preview" className="preview-image" />
                ) : (
                  <video src={previewUrl} controls className="preview-video" />
                )}
              </div>
              <div className="preview-info">
                <p><strong>File:</strong> {selectedFile.name}</p>
                <p><strong>Size:</strong> {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
              </div>
              <div className="modal-actions vibe-upload-actions">
                <button type="button" className="cancel-btn" onClick={cancelUploadFromPreview}>
                  Cancel
                </button>
                <button type="button" className="confirm-btn vibe-upload-submit" onClick={closePreviewModal}>
                  Use This {uploadType === 'image' ? 'Image' : 'Video'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default VibeUploadModal;
